// MAKING THE SIGNATURE'S PNG IN THE BROWSER, AS THE APP MAKES ITS OWN (WEB-PARITY-6; SignatureEditor.swift `save()`).
//
//   Draw: the app renders only the inked region, inset by 8 points, at a fixed 3× raster, and recolours the strokes
//         BLACK for the white page (the canvas ink is light, for the dark sheet). So does this.
//   Type: the app sets the typed name in Snell Roundhand Bold at 64 points with 12/8 points of padding, at 3×. A browser
//         has that face only where the system does (Apple's); elsewhere the CSS `cursive` family stands in — a finding,
//         not a guess at a web font.
//
// Chrome only: these decide nothing a server reads except the picture itself, which the server checks again
// (functions/src/v2/letter/pdf/signature.ts). A canvas this environment cannot make (a test's) answers null.

import type { HeldSignature } from "./signature";

export interface Stroke {
  readonly points: readonly { readonly x: number; readonly y: number }[];
}

const SCALE = 3;
/** PKInkingTool(.pen, width: 3). */
const PEN_WIDTH = 3;
/** SignatureEditor.swift: `.font(.custom("SnellRoundhand-Bold", size: 64))`. */
export const SIGNATURE_FONT = '"Snell Roundhand", "SnellRoundhand-Bold", "Apple Chancery", cursive';

function toHeld(canvas: HTMLCanvasElement): HeldSignature | null {
  let url: string;
  try {
    url = canvas.toDataURL("image/png");
  } catch {
    return null;
  }
  const comma = url.indexOf(",");
  if (!url.startsWith("data:image/png") || comma < 0) return null;
  return { pngBase64: url.slice(comma + 1), width: canvas.width, height: canvas.height };
}

/** The drawn strokes' inked region, inset by 8, at 3×, in black. Null for no ink or no canvas. */
export function drawnSignaturePng(strokes: readonly Stroke[], doc: Document = document): HeldSignature | null {
  const points = strokes.flatMap((s) => s.points);
  if (points.length === 0) return null;
  const minX = Math.min(...points.map((p) => p.x)) - 8;
  const minY = Math.min(...points.map((p) => p.y)) - 8;
  const maxX = Math.max(...points.map((p) => p.x)) + 8;
  const maxY = Math.max(...points.map((p) => p.y)) + 8;
  const canvas = doc.createElement("canvas");
  canvas.width = Math.max(1, Math.round((maxX - minX) * SCALE));
  canvas.height = Math.max(1, Math.round((maxY - minY) * SCALE));
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;
  ctx.scale(SCALE, SCALE);
  ctx.translate(-minX, -minY);
  ctx.strokeStyle = "#000000";
  ctx.fillStyle = "#000000";
  ctx.lineWidth = PEN_WIDTH;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const stroke of strokes) {
    const [first, ...rest] = stroke.points;
    if (first === undefined) continue;
    if (rest.length === 0) {
      ctx.beginPath();
      ctx.arc(first.x, first.y, PEN_WIDTH / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (const p of rest) ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  return toHeld(canvas);
}

/** The typed name set as a signature, at 3×, in black on transparent. Null for no name or no canvas. */
export function typedSignaturePng(name: string, doc: Document = document): HeldSignature | null {
  const text = name.trim();
  if (text.length === 0) return null;
  const measure = doc.createElement("canvas").getContext("2d");
  if (measure === null) return null;
  const font = `bold 64px ${SIGNATURE_FONT}`;
  measure.font = font;
  const metrics = measure.measureText(text);
  const ascent = metrics.actualBoundingBoxAscent || 64 * 0.8;
  const descent = metrics.actualBoundingBoxDescent || 64 * 0.3;
  const width = Math.ceil(metrics.width) + 24;
  const height = Math.ceil(ascent + descent) + 16;
  const canvas = doc.createElement("canvas");
  canvas.width = width * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;
  ctx.scale(SCALE, SCALE);
  ctx.font = font;
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, 12, 8 + ascent);
  return toHeld(canvas);
}
